/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { cn } from '../../lib/utils/cn.js';

import type { JSX } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';

export interface FormFieldProps {
  readonly id: string;
  readonly type: string;
  readonly label: string;
  readonly autoComplete: string;
  readonly inputMode?: 'numeric' | undefined;
  readonly placeholder?: string | undefined;
  readonly error?: string | undefined;
  readonly registration: UseFormRegisterReturn;
  /** Visual variant — light (customer surfaces) or dark (staff console). */
  readonly variant?: 'light' | 'dark' | undefined;
  /** Optional element rendered inside the input's right edge (e.g. eye toggle). */
  readonly trailing?: JSX.Element | undefined;
}

/**
 * Standard labelled input with RHF registration, error display, and a11y
 * wiring. The single source of form-input markup across both login surfaces —
 * keeps individual page components small and consistent.
 */
export function FormField({
  id,
  type,
  label,
  autoComplete,
  inputMode,
  placeholder,
  error,
  registration,
  variant = 'light',
  trailing,
}: FormFieldProps): JSX.Element {
  const dark = variant === 'dark';
  return (
    <div>
      <label
        htmlFor={id}
        className={cn(
          'mb-1.5 block text-sm font-medium',
          dark ? 'text-gray-300' : 'text-slate-700',
        )}
      >
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={type}
          autoComplete={autoComplete}
          inputMode={inputMode}
          placeholder={placeholder}
          className={cn(
            'focus-ring w-full rounded-xl border px-4 py-3 text-sm',
            trailing !== undefined && 'pr-12',
            dark
              ? 'border-gray-700 bg-gray-800 text-white placeholder:text-gray-500'
              : 'border-slate-200 bg-slate-50',
          )}
          aria-invalid={error !== undefined}
          {...registration}
        />
        {trailing}
      </div>
      {error !== undefined && (
        <p role="alert" className={cn('mt-1 text-xs', dark ? 'text-red-400' : 'text-red-600')}>
          {error}
        </p>
      )}
    </div>
  );
}
